import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, LayoutGrid } from 'lucide-react';
import axiosInstance from '../../api/axios';
import { useRcmAllTablePage } from '../../hooks/rcm/useRcmAllTablePage.ts';
import { fetchSoxCycles } from '../../request/sox/soxDashboard.api.js';
import { normalizePageSize, readStoredListPageSize, writeStoredListPageSize } from '../../shared/table/pagination.ts';
import { TablePaginationFooter } from '../../shared/table/TablePaginationFooter.tsx';
import { RcmColumnVisibilityMenu } from './RcmColumnVisibilityMenu.jsx';
import { RcmAllListFilters } from './RcmAllListFilters.jsx';
import { useRcmAllListFilters } from './useRcmAllListFilters.js';
import {
  RCM_ALL_TABLE_COLUMNS,
  createDefaultRcmAllColumnVisibility,
  getDefaultRcmAllColumnOrder,
  getHideableRcmAllColumns,
  getOrderedVisibleRcmAllColumnIds,
  getVisibleRcmAllColumnCount,
  isRcmAllColumnVisible,
  mergeRcmAllHideableOrder,
} from './rcmAllTableColumnConfig.js';
import {
  clearStoredRcmColumnPreferences,
  readStoredRcmColumnOrder,
  readStoredRcmColumnVisibility,
  writeStoredRcmColumnOrder,
  writeStoredRcmColumnVisibility,
} from './rcmColumnPreferences.ts';
import { mapAllApiItemToRow, formatRcmKeyReportsPreview } from './rcmAllRowMappers.js';
import { RISK_CATEGORY_BADGE_CLASSES } from '../../constants/riskCategories';
import { useAuditYear } from '../../contexts/AuditYearContext.tsx';

const RCM_ALL_COLUMN_IDS = RCM_ALL_TABLE_COLUMNS.map((column) => column.id);

function Th({ children, className = '', style }) {
  return (
    <th
      className={`px-2.5 py-2 text-[11px] leading-tight font-semibold text-left whitespace-nowrap border-r border-gray-700 ${className}`}
      style={style}
    >
      {children}
    </th>
  );
}

function Td({ children, className = '', title }) {
  return (
    <td className={`px-2.5 py-2 text-xs align-top border-r border-gray-100 ${className}`} title={title}>
      {children}
    </td>
  );
}

function columnLabel(columnId) {
  return RCM_ALL_TABLE_COLUMNS.find((c) => c.id === columnId)?.label || columnId;
}

function renderCell(row, columnId) {
  const value = row[columnId];
  switch (columnId) {
    case 'srNo':
      return <Td key={columnId} className="text-center text-gray-600 font-medium">{row.srNo}</Td>;
    case 'classification':
      return (
        <Td key={columnId}>
          <span
            className={`inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium ${
              String(row.classification).toLowerCase() === 'itgc'
                ? 'bg-indigo-50 text-indigo-700'
                : 'bg-teal-50 text-teal-700'
            }`}
          >
            {row.classification || '-'}
          </span>
        </Td>
      );
    case 'domain':
      return (
        <Td key={columnId}>
          {row.domain !== '-' ? (
            <span className="inline-flex items-center rounded bg-teal-50 px-2 py-0.5 text-[11px] font-medium text-teal-700">
              {row.domain}
            </span>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </Td>
      );
    case 'system':
      return (
        <Td key={columnId} title={row.system}>
          <span className={row.system !== '-' ? 'line-clamp-2 text-indigo-700 font-medium' : 'text-gray-400'}>
            {row.system}
          </span>
        </Td>
      );
    case 'controlId':
      return <Td key={columnId} className="font-mono text-blue-700">{value}</Td>;
    case 'riskId':
      return <Td key={columnId} className="font-mono text-gray-600">{value}</Td>;
    case 'keyNonKey':
      return (
        <Td key={columnId} className="text-center">
          {value === '-' ? (
            <span className="text-gray-400">-</span>
          ) : (
            <span
              className={`text-[11px] font-semibold px-2 py-0.5 rounded ${
                value === 'Key' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {value}
            </span>
          )}
        </Td>
      );
    case 'riskCategory': {
      if (value === '-') {
        return <Td key={columnId} className="text-center"><span className="text-gray-400">-</span></Td>;
      }
      const badgeClass = RISK_CATEGORY_BADGE_CLASSES[value] || 'bg-gray-100 text-gray-700';
      return (
        <Td key={columnId} className="text-center">
          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${badgeClass}`}>
            {value}
          </span>
        </Td>
      );
    }
    case 'completeness':
    case 'existenceOccurrence':
    case 'accuracy':
    case 'valuation':
    case 'rightsObligations':
      return <Td key={columnId} className="text-center text-gray-600">{value}</Td>;
    case 'keyReports': {
      const { display, fullLabel, isCompact } = formatRcmKeyReportsPreview(value);
      return (
        <Td key={columnId} title={isCompact ? fullLabel : undefined}>
          <span className={display === '-' ? 'text-gray-400' : 'text-gray-700'}>{display}</span>
        </Td>
      );
    }
    default:
      return (
        <Td key={columnId} title={value !== '-' ? value : undefined}>
          <span className={value === '-' ? 'text-gray-400' : 'text-gray-700'}>{value}</span>
        </Td>
      );
  }
}

export default function RcmAllView({ onStatsChange, onListParamsChange, onExportColumnsChange }) {
  const { selectedAuditYearId } = useAuditYear();
  const [listPage, setListPage] = useState(1);
  const [pageSize, setPageSize] = useState(readStoredListPageSize);
  const [columnVisibility, setColumnVisibility] = useState(() =>
    readStoredRcmColumnVisibility('all', createDefaultRcmAllColumnVisibility, RCM_ALL_COLUMN_IDS)
  );
  const [columnOrder, setColumnOrder] = useState(() =>
    readStoredRcmColumnOrder('all', getDefaultRcmAllColumnOrder, RCM_ALL_COLUMN_IDS)
  );
  const [soxCycleOptions, setSoxCycleOptions] = useState([{ value: 'all', label: 'All Cycles' }]);
  const [domainOptions, setDomainOptions] = useState([{ value: 'all', label: 'All Domains' }]);
  const [systemOptions, setSystemOptions] = useState([{ value: 'all', label: 'All Systems' }]);

  const {
    searchQuery,
    setSearchQuery,
    filterClassification,
    setFilterClassification,
    filterSoxCycle,
    setFilterSoxCycle,
    filterDomain,
    setFilterDomain,
    filterSystem,
    setFilterSystem,
    filterRiskCategory,
    setFilterRiskCategory,
    resetFilters,
    apiFilterParams,
    hasActiveListCriteria,
    domainFilterEnabled,
    systemFilterEnabled,
    soxCycleFilterEnabled,
    riskCategoryFilterEnabled,
  } = useRcmAllListFilters();

  const safePageSize = normalizePageSize(pageSize);
  const listParams = useMemo(
    () => ({
      page: listPage,
      pageSize: safePageSize,
      ...apiFilterParams,
      auditYearId: selectedAuditYearId || undefined,
    }),
    [listPage, safePageSize, apiFilterParams, selectedAuditYearId]
  );

  useEffect(() => {
    onListParamsChange?.(listParams);
  }, [listParams, onListParamsChange]);

  useEffect(() => {
    setListPage(1);
  }, [apiFilterParams, selectedAuditYearId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cycles = await fetchSoxCycles();
        if (cancelled) return;
        setSoxCycleOptions([
          { value: 'all', label: 'All Cycles' },
          ...cycles
            .map((c) => {
              const value = String(c?.cycle_id ?? '').trim();
              if (!value) return null;
              const name = String(c?.name ?? '').trim();
              const label = name ? `${value} - ${name}` : value;
              return { value, label };
            })
            .filter(Boolean),
        ]);
      } catch {
        if (!cancelled) setSoxCycleOptions([{ value: 'all', label: 'All Cycles' }]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get('/api/itgc-domains', {
          params: { page: 1, page_size: 100, sort_by: 'code', sort_order: 'asc', status: 'active' },
        });
        if (cancelled) return;
        const body = res.data ?? {};
        const root = body?.data && typeof body.data === 'object' ? body.data : body;
        const rawItems = Array.isArray(root?.domains) ? root.domains : [];
        setDomainOptions([
          { value: 'all', label: 'All Domains' },
          ...rawItems
            .map((row) => {
              const code = String(row?.code ?? '').trim();
              if (!code) return null;
              return { value: code, label: code };
            })
            .filter(Boolean),
        ]);
      } catch {
        if (!cancelled) setDomainOptions([{ value: 'all', label: 'All Domains' }]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get('/api/systems', { params: { page_size: 200 } });
        if (cancelled) return;
        const items = Array.isArray(res.data?.systems) ? res.data.systems : [];
        setSystemOptions([
          { value: 'all', label: 'All Systems' },
          ...items
            .map((s) => {
              const value = String(s?.id ?? '').trim();
              if (!value) return null;
              const name = String(s?.name ?? '').trim() || value;
              const label = name.length > 20 ? `${name.slice(0, 20)}...` : name;
              return { value, label };
            })
            .filter(Boolean)
            .sort((a, b) => a.label.localeCompare(b.label)),
        ]);
      } catch {
        if (!cancelled) setSystemOptions([{ value: 'all', label: 'All Systems' }]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const { data, isFetching, isLoading, isPlaceholderData } = useRcmAllTablePage(listParams);

  const totalItems = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const tableLoading = isFetching && (!data || isPlaceholderData);

  const rows = useMemo(() => {
    const items = data?.items ?? [];
    const offset = (listPage - 1) * safePageSize;
    return items
      .map((item, index) => mapAllApiItemToRow(item, offset + index + 1))
      .filter(Boolean);
  }, [data?.items, listPage, safePageSize]);

  const keyControlsInView = useMemo(
    () => rows.filter((r) => r.keyNonKey === 'Key').length,
    [rows]
  );

  useEffect(() => {
    onStatsChange?.({ rows: totalItems, keyControls: keyControlsInView });
  }, [onStatsChange, totalItems, keyControlsInView]);

  const visibleColumnCount = useMemo(
    () => getVisibleRcmAllColumnCount(columnVisibility),
    [columnVisibility]
  );

  const orderedVisibleColumnIds = useMemo(
    () => getOrderedVisibleRcmAllColumnIds(columnOrder, columnVisibility),
    [columnOrder, columnVisibility]
  );

  // Keep parent Excel export aligned with gear column visibility + order.
  const onExportColumnsChangeRef = useRef(onExportColumnsChange);
  useEffect(() => { onExportColumnsChangeRef.current = onExportColumnsChange; }, [onExportColumnsChange]);
  useEffect(() => {
    onExportColumnsChangeRef.current?.(orderedVisibleColumnIds);
  }, [orderedVisibleColumnIds]);

  const paginationFrom = totalItems === 0 ? 0 : (listPage - 1) * safePageSize + 1;
  const paginationTo = Math.min(listPage * safePageSize, totalItems);
  const safePage = Math.min(Math.max(1, listPage), Math.max(1, totalPages));

  const handleToggleColumn = useCallback((columnId) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [columnId]: !isRcmAllColumnVisible(prev, columnId),
    }));
  }, []);

  const handleReorderColumns = useCallback((orderedHideableIds) => {
    setColumnOrder((prev) => mergeRcmAllHideableOrder(prev, orderedHideableIds));
  }, []);

  const handleResetColumns = useCallback(() => {
    clearStoredRcmColumnPreferences('all');
    setColumnVisibility(createDefaultRcmAllColumnVisibility());
    setColumnOrder(getDefaultRcmAllColumnOrder());
  }, []);

  useEffect(() => {
    writeStoredRcmColumnVisibility('all', columnVisibility);
  }, [columnVisibility]);

  useEffect(() => {
    writeStoredRcmColumnOrder('all', columnOrder);
  }, [columnOrder]);

  const handlePaginationChange = useCallback(({ pageIndex, pageSize: nextSize }) => {
    if (nextSize != null && nextSize !== safePageSize) {
      const normalized = normalizePageSize(nextSize);
      writeStoredListPageSize(normalized);
      setPageSize(normalized);
      setListPage(1);
      return;
    }
    setListPage(pageIndex + 1);
  }, [safePageSize]);

  const filterBar = (
    <RcmAllListFilters
      searchQuery={searchQuery}
      onSearchQueryChange={setSearchQuery}
      filterClassification={filterClassification}
      onFilterClassificationChange={setFilterClassification}
      filterSoxCycle={filterSoxCycle}
      onFilterSoxCycleChange={setFilterSoxCycle}
      soxCycleOptions={soxCycleOptions}
      filterDomain={filterDomain}
      onFilterDomainChange={setFilterDomain}
      domainOptions={domainOptions}
      domainFilterEnabled={domainFilterEnabled}
      filterSystem={filterSystem}
      onFilterSystemChange={setFilterSystem}
      systemOptions={systemOptions}
      systemFilterEnabled={systemFilterEnabled}
      filterRiskCategory={filterRiskCategory}
      onFilterRiskCategoryChange={setFilterRiskCategory}
      riskCategoryFilterEnabled={riskCategoryFilterEnabled}
      soxCycleFilterEnabled={soxCycleFilterEnabled}
      onReset={resetFilters}
      columnVisibilityMenu={
        <RcmColumnVisibilityMenu
          columns={getHideableRcmAllColumns()}
          columnVisibility={columnVisibility}
          onToggleColumn={handleToggleColumn}
          onReset={handleResetColumns}
          enableReorder
          columnOrder={columnOrder}
          onReorder={handleReorderColumns}
        />
      }
    />
  );

  if (isLoading && rows.length === 0) {
    return (
      <div className="flex-1 min-h-0 flex flex-col">
        {filterBar}
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 text-teal-600 animate-spin" />
            <p className="text-sm text-gray-500">Loading combined RCM…</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isLoading && totalItems === 0 && !hasActiveListCriteria) {
    return (
      <div className="flex-1 min-h-0 flex flex-col">
        {filterBar}
        <div className="bg-white rounded-lg border border-gray-200 p-10 text-center">
          <div className="w-16 h-16 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-4">
            <LayoutGrid className="h-8 w-8 text-teal-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">No RCM rows yet</h2>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            Combined Process and ITGC rows will appear here once mappings exist in either tab.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {filterBar}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm flex-1 min-h-0 flex flex-col overflow-hidden">
        <div className="relative flex-1 min-h-0 overflow-x-auto overflow-y-auto">
          {tableLoading ? (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60" aria-busy="true">
              <Loader2 className="h-8 w-8 text-teal-600 animate-spin" />
            </div>
          ) : null}
          <table className="min-w-[2200px] w-full border-separate border-spacing-0">
            <thead className="sticky top-0 z-30 shadow-md">
              <tr className="bg-gray-900 text-white">
                {orderedVisibleColumnIds.map((columnId) => {
                  const label = columnLabel(columnId);
                  if (columnId === 'srNo') return <Th key={columnId} className="text-center">{label}</Th>;
                  if (['keyNonKey', 'riskCategory', 'completeness', 'existenceOccurrence', 'accuracy', 'valuation', 'rightsObligations'].includes(columnId)) {
                    return <Th key={columnId} className="text-center">{label}</Th>;
                  }
                  return (
                    <Th
                      key={columnId}
                      style={{ minWidth: columnId.includes('Description') || columnId === 'controlObjective' ? '220px' : undefined }}
                    >
                      {label}
                    </Th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={Math.max(visibleColumnCount, 1)} className="px-6 py-10 text-center text-sm text-gray-500">
                    {hasActiveListCriteria ? 'No rows match the current filters.' : 'No rows on this page.'}
                  </td>
                </tr>
              ) : rows.map((row) => (
                <tr
                  key={row.sourceId || `${row.rowType}-${row.srNo}`}
                  className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${row.srNo % 2 === 0 ? 'bg-gray-50/50' : 'bg-white'}`}
                >
                  {orderedVisibleColumnIds.map((columnId) => renderCell(row, columnId))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalItems > 0 || hasActiveListCriteria ? (
          <TablePaginationFooter
            className="shrink-0 border-t border-gray-200"
            from={paginationFrom}
            to={paginationTo}
            total={totalItems}
            itemLabel="RCM rows"
            pageIndex={safePage - 1}
            pageCount={Math.max(1, totalPages)}
            canPreviousPage={safePage > 1}
            canNextPage={safePage < totalPages}
            onPreviousPage={() => handlePaginationChange({ pageIndex: safePage - 2 })}
            onNextPage={() => handlePaginationChange({ pageIndex: safePage })}
            showPageSizeSelector
            pageSize={safePageSize}
            onPageSizeChange={(nextSize) => handlePaginationChange({ pageIndex: 0, pageSize: nextSize })}
          />
        ) : null}
      </div>
    </div>
  );
}
